import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  const companyName = "OnRounds AI";
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Minimum Necessary Use and Disclosure Policy
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Minimum Necessary Use and Disclosure Policy</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Typography variant="body1" paragraph>
            The Minimum Necessary Use and Disclosure Policy is designed to ensure that all uses, disclosures, and
            requests for Protected Health Information (PHI) by {companyName} are confined to the least amount of
            information necessary to accomplish the intended purpose. This adherence to the Health Insurance Portability
            and Accountability Act (HIPAA) Privacy Rule underlines our commitment to safeguarding the privacy and
            security of health information.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Purpose
          </Typography>
          <Typography variant="body1" paragraph>
            This policy serves to limit the exposure of PHI to the minimum necessary to achieve the desired outcome,
            minimizing the risk of unauthorized access or disclosure. It applies to all internal uses, external
            disclosures, and requests for PHI, excluding certain exceptions such as disclosures for treatment purposes.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Scope
          </Typography>
          <Typography variant="body1" paragraph>
            This policy is applicable to all employees, contractors, and business associates of {companyName} who have
            access to PHI, ensuring that PHI handling procedures are in line with HIPAA regulations.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Policy Statement
          </Typography>
          <List>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>General Requirement:</b> Reasonable efforts must be made to use, disclose, or request only the
                  minimum amount of PHI necessary to accomplish the intended purpose.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Use of PHI Within {companyName}:</b> Access to PHI within the company is restricted to those
                  employees who need it to perform their job functions.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Disclosures of PHI Outside {companyName}:</b> Disclosures outside the company are limited to the
                  minimum necessary for the specific purpose of the disclosure.
                </Typography>
              </Box>
            </ListItem>
            <ListItem alignItems="flex-start">
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Exceptions:</b> This policy lists exceptions such as disclosures for treatment and other specific
                  circumstances where the minimum necessary standard does not apply.
                </Typography>
              </Box>
            </ListItem>
          </List>

          <Typography variant="h5" gutterBottom>
            Compliance and Enforcement
          </Typography>
          <Typography variant="body1" paragraph>
            The Privacy Officer is tasked with monitoring compliance with this policy. Non-compliance will be addressed
            according to the established Sanction Policy for Privacy Violations, ensuring that {companyName} maintains
            stringent protection of PHI.
          </Typography>

          <Typography variant="h5" gutterBottom>
            Documentation
          </Typography>
          <Typography variant="body1" paragraph>
            Documentation of uses, disclosures, and requests of PHI under this policy must be maintained, including the
            justification for the determination of "minimum necessary," reinforcing our dedication to the principles of
            privacy and security as mandated by HIPAA.
          </Typography>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
